import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Calendar, 
  Clock, 
  Upload, 
  FileText, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Download,
  Eye,
  Settings,
  LogOut
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { HomeworkAssignment, HomeworkSubmission } from '../types';
import { homeworkService, homeworkSubmissionService } from '../services/apiService';
import toast from 'react-hot-toast';

const StudentHomeworkPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<HomeworkAssignment[]>([]);
  const [submissions, setSubmissions] = useState<HomeworkSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState<HomeworkAssignment | null>(null);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<HomeworkSubmission | null>(null);
  const [submissionData, setSubmissionData] = useState({
    content: '',
    attachments: [] as File[]
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  useEffect(() => {
    if (user?.role === 'student') {
      loadStudentHomework();
    }
  }, [user]);

  const loadStudentHomework = async () => {
    setLoading(true);
    try {
      // Load homework assignments
      const homeworkData = await homeworkService.getCurrentStudentHomework();
      
      // Handle both response formats: student format {assignments, submissions} or teacher format [assignments]
      if (Array.isArray(homeworkData)) {
        // Teacher format - just assignments array
        setAssignments(homeworkData);
      } else if (homeworkData && homeworkData.assignments) {
        // Student format - object with assignments and submissions
        setAssignments(homeworkData.assignments);
      } else {
        // Fallback
        setAssignments([]);
      }
      
      // Load submissions separately
      try {
        const submissionsData = await homeworkSubmissionService.getCurrentStudentSubmissions();
        setSubmissions(submissionsData);
      } catch (error) {
        console.warn('Could not load submissions:', error);
        setSubmissions([]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading homework:', error);
      toast.error('Failed to load homework assignments');
      setLoading(false);
    }
  };

  const handleSubmitHomework = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedAssignment) return;

    try {
      // Convert File objects to file names for now (in a real app, you'd upload files to a server)
      const attachmentNames = submissionData.attachments.map(file => file.name);
      
      await homeworkSubmissionService.submit(
        selectedAssignment.id, 
        submissionData.content, 
        submissionData.attachments
      );
      toast.success('Homework submitted successfully!');
      setShowSubmissionModal(false);
      setSelectedAssignment(null);
      setSubmissionData({ content: '', attachments: [] });
      loadStudentHomework();
    } catch (error) {
      console.error('Error submitting homework:', error);
      toast.error('Failed to submit homework');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSubmissionData(prev => ({
        ...prev,
        attachments: Array.from(e.target.files || [])
      }));
    }
  };

  const downloadAttachment = (dataUrl: string, fileName: string) => {
    try {
      // Convert base64 data URL to blob
      fetch(dataUrl)
        .then(res => res.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        })
        .catch(err => {
          console.error('Error downloading file:', err);
          toast.error('Failed to download file');
        });
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const getSubmissionStatus = (assignment: HomeworkAssignment) => {
    const submission = submissions.find(s => s.assignmentId === assignment.id);
    const now = new Date();
    const dueDate = new Date(assignment.dueDate);
    
    if (submission) {
      return {
        status: submission.status,
        text: submission.status === 'graded' ? 'Graded' : 'Submitted',
        color: submission.status === 'graded' ? 'text-green-600' : 'text-blue-600',
        bgColor: submission.status === 'graded' ? 'bg-green-100' : 'bg-blue-100',
        icon: submission.status === 'graded' ? CheckCircle : CheckCircle
      };
    } else if (dueDate < now) {
      return {
        status: 'overdue',
        text: 'Overdue',
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        icon: XCircle
      };
    } else {
      return {
        status: 'pending',
        text: 'Not Submitted',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        icon: AlertCircle
      };
    }
  };

  const getTimeRemaining = (dueDate: Date) => {
    const now = new Date();
    const diff = dueDate.getTime() - now.getTime();
    
    if (diff <= 0) return 'Overdue';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} remaining`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} remaining`;
    return 'Due soon';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading homework assignments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <BookOpen className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Homework Assignments
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  View and submit your homework assignments
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/student-profile')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <Settings className="w-4 h-4 mr-2" />
                Profile
              </button>
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {assignments.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              No homework assignments
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Your homework assignments will appear here when your teachers post them.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {assignments.map((assignment) => {
              const status = getSubmissionStatus(assignment);
              const StatusIcon = status.icon;
              const submission = submissions.find(s => s.assignmentId === assignment.id);
              
              return (
                <div key={assignment.id} className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-shadow">
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                          {assignment.title}
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400 mb-3">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {new Date(assignment.dueDate).toLocaleDateString()}
                          </div>
                          <div className="flex items-center">
                            <FileText className="w-4 h-4 mr-1" />
                            {assignment.maxPoints} points
                          </div>
                        </div>
                        {assignment.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                            {assignment.description}
                          </p>
                        )}
                        <div className="flex items-center justify-between">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color} ${status.bgColor}`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.text}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {getTimeRemaining(new Date(assignment.dueDate))}
                          </span>
                        </div>
                        
                        {/* Show attachments if submitted */}
                        {submission && submission.attachments && (
                          <div className="mt-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                              <Download className="w-3 h-3 mr-1 inline" />
                              Attachments:
                            </div>
                            <div className="space-y-1">
                              {(() => {
                                try {
                                  const attachments = JSON.parse(submission.attachments || '[]');
                                  if (Array.isArray(attachments) && attachments.length > 0) {
                                    return attachments.map((attachment, index) => {
                                      const fileName = attachment.originalName || `Attachment ${index + 1}`;
                                      const isImage = fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/);
                                      
                                      return (
                                        <div key={index} className="flex items-center space-x-2">
                                          {isImage && attachment.data ? (
                                            <div>
                                              <img 
                                                src={attachment.data} 
                                                alt={fileName}
                                                className="max-w-xs max-h-48 rounded"
                                              />
                                              <button
                                                onClick={() => attachment.data && downloadAttachment(attachment.data, fileName)}
                                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-xs flex items-center mt-1 cursor-pointer"
                                              >
                                                <Download className="w-3 h-3 mr-1" />
                                                {fileName}
                                              </button>
                                            </div>
                                          ) : (
                                            <button
                                              onClick={() => attachment.data && downloadAttachment(attachment.data, fileName)}
                                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-xs flex items-center cursor-pointer"
                                            >
                                              <Download className="w-3 h-3 mr-1" />
                                              {fileName}
                                            </button>
                                          )}
                                        </div>
                                      );
                                    });
                                  }
                                  return <span className="text-xs text-gray-500">No attachments</span>;
                                } catch {
                                  return <span className="text-xs text-gray-500">No attachments</span>;
                                }
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-4 flex space-x-2">
                      {status.status === 'pending' && (
                        <button
                          onClick={() => {
                            setSelectedAssignment(assignment);
                            setShowSubmissionModal(true);
                          }}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Submit
                        </button>
                      )}
                      {submission && (
                        <button
                          onClick={() => {
                            setSelectedSubmission(submission);
                            setShowViewModal(true);
                          }}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Submission Modal */}
        {showSubmissionModal && selectedAssignment && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Submit Homework: {selectedAssignment.title}
                </h3>
                <form onSubmit={handleSubmitHomework} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Assignment Details
                    </label>
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        <strong>Due Date:</strong> {new Date(selectedAssignment.dueDate).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        <strong>Points:</strong> {selectedAssignment.maxPoints}
                      </p>
                      {selectedAssignment.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                          <strong>Description:</strong> {selectedAssignment.description}
                        </p>
                      )}
                      {selectedAssignment.instructions && (
                        <div className="mt-2">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Instructions:</p>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                            {selectedAssignment.instructions}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Your Response
                    </label>
                    <textarea
                      value={submissionData.content}
                      onChange={(e) => setSubmissionData(prev => ({ ...prev, content: e.target.value }))}
                      rows={6}
                      className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Type your homework response here..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Attachments
                    </label>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="mt-1 block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {submissionData.attachments.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Selected files: {submissionData.attachments.map(f => f.name).join(', ')}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowSubmissionModal(false);
                        setSelectedAssignment(null);
                        setSubmissionData({ content: '', attachments: [] });
                      }}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Submit Homework
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* View Submission Modal */}
        {showViewModal && selectedSubmission && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Your Submission
                  </h3>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Submitted Content
                    </label>
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {selectedSubmission.content || 'No text provided.'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      <Download className="w-3 h-3 mr-1 inline" />
                      Attachments:
                    </div>
                    <div className="space-y-2">
                      {(() => {
                        try {
                          let parsed: any[] = [];
                          if (typeof selectedSubmission.attachments === 'string') {
                            parsed = JSON.parse(selectedSubmission.attachments || '[]');
                          } else if (Array.isArray(selectedSubmission.attachments)) {
                            parsed = selectedSubmission.attachments as any[];
                          }

                          if (Array.isArray(parsed) && parsed.length > 0) {
                            return parsed.map((att: any, idx: number) => {
                              const fileName = att.originalName || att.fileName || `Attachment ${idx + 1}`;
                              const dataUrl = att.data || att.dataUrl || '';
                              const isImage = fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/);

                              return (
                                <div key={idx} className="flex items-start space-x-3">
                                  {isImage && dataUrl ? (
                                    <img
                                      src={dataUrl}
                                      alt={fileName}
                                      className="max-w-[160px] max-h-40 rounded"
                                    />
                                  ) : null}
                                  <button
                                    onClick={() => dataUrl && downloadAttachment(dataUrl, fileName)}
                                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm flex items-center cursor-pointer"
                                  >
                                    <Download className="w-4 h-4 mr-2" />
                                    {fileName}
                                  </button>
                                </div>
                              );
                            });
                          }
                          return <span className="text-xs text-gray-500">No attachments</span>;
                        } catch (e) {
                          console.error('Error parsing attachments:', e, selectedSubmission.attachments);
                          return <span className="text-xs text-gray-500">Error loading attachments</span>;
                        }
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentHomeworkPage;

